/**
 * Zweimal tippen, um eine Prüfung zu löschen.
 *
 * **Warum überhaupt.** `DeleteExamDialog` fragt absichtlich nur nach, wenn an der Prüfung
 * etwas hängt — eine Rückfrage ohne Inhalt erzieht dazu, Rückfragen wegzutippen. Eine frisch
 * geladene Prüfung hat aber noch keine Protokollzeile und war damit bei einem einzigen
 * Fehlgriff sofort weg. Das Doppeltippen schliesst genau diese Lücke, ohne die Rückfrage zu
 * verwässern: der erste Tipp schaltet nur scharf und zeigt es, der zweite löst aus.
 *
 * Die Entscheidung steht hier als reine Funktion, damit sie prüfbar ist — die Anzeige hält
 * nur noch den Zustand und einen Zeitgeber fürs Aussehen. Ohne Uhr im Modul lässt sich jeder
 * Grenzfall mit einer Zahl beschreiben statt mit Warten.
 */

/** Wie lange der Papierkorb scharf bleibt. */
export const LOESCH_FRIST_MS = 2000;

/** Welche Zeile scharf ist und seit wann. `null` heisst: keine. */
export type LoeschScharf = { id: string; seit: number } | null;

export type Tippfolge =
  | { aktion: 'scharf'; scharf: LoeschScharf }
  | { aktion: 'loeschen'; scharf: null };

/**
 * @param scharf  Was bisher scharf war.
 * @param id      Die getippte Zeile.
 * @param jetzt   Zeitpunkt des Tipps in ms.
 * @param frist   Wie lange scharf gilt; nur für Tests anders als `LOESCH_FRIST_MS`.
 */
export function tippen(
  scharf: LoeschScharf,
  id: string,
  jetzt: number,
  frist: number = LOESCH_FRIST_MS,
): Tippfolge {
  const trifftDieselbeZeile = scharf !== null && scharf.id === id;
  // `<=`: die Grenze gehört dem Prüfer. Wer genau auf der Frist tippt, hat getroffen.
  const nochInDerFrist = trifftDieselbeZeile && jetzt - scharf.seit <= frist;

  if (nochInDerFrist) return { aktion: 'loeschen', scharf: null };

  // Alles andere zählt als erster Tipp — auch der zweite auf dieselbe Zeile nach Ablauf, und
  // der auf eine andere Zeile. Letzteres entwaffnet die vorige: zwei scharfe Papierkörbe
  // nebeneinander wären eine Falle.
  return { aktion: 'scharf', scharf: { id, seit: jetzt } };
}
